import type { TFunction } from "i18next";
import {
  isKnownGeocodeErrorCode,
  type GeocodingFailureItem,
} from "@/types/orders";

/**
 * Parsing + grouping for the geocoding failure breakdown (TASK-4744 payload).
 *
 * This module is the ONE place a raw `GEOCODING_*` registry code becomes
 * user-visible text. The progress toast and `OrderDetailModal`'s amber badge
 * both route through {@link translateGeocodeErrorCode}, so a dispatcher cannot
 * meet "Address not found" in the toast and different wording in the modal, and
 * a NEW backend code has exactly one place to be handled.
 *
 * ## Why this is a sibling of `unassignedBreakdown.ts` rather than a reuse of it
 *
 * TASK-4745 asks whether TASK-4741's grouped renderer fits. Its GROUPING
 * contract does not, on three counts:
 *
 *  1. `UnassignedBreakdown` carries `unassigned_reason_counts` — an authoritative
 *     per-reason total computed server-side over the WHOLE build, which is what
 *     lets it stay truthful past the item cap and render a group that has no
 *     named orders at all. The geocoding payload has no per-reason counts: only
 *     a flat `failed` total and a capped `failed_items`. A group total here can
 *     only ever be the number of items actually named, so `hiddenCount` is a
 *     property of the WHOLE breakdown, never of a group.
 *  2. Its reason is a required non-null solver string. A geocoding `code` is
 *     legitimately `null` — pre-TASK-4744 rows were deliberately not backfilled
 *     — which needs a distinct "reason not recorded" bucket rather than being
 *     coerced to a fake code.
 *  3. It pairs every reason with an actionable `suggestions.*` entry. Geocoding
 *     advice is folded into the message copy itself, so a suggestion slot would
 *     be permanently undefined.
 *
 * Generalizing over those differences would mean an optional-counts parameter,
 * a nullable reason key and an optional suggestion lookup — three conditionals
 * threaded through a shared function to serve two callers, which is harder to
 * read than either concrete version. What genuinely IS shared is the display
 * convention (title → address → `#id`) and the "unknown code renders prose, not
 * a key path" rule, and both are mirrored here deliberately.
 */

/** One code and every failed order that carried it. */
export interface GeocodeFailureGroup {
  /**
   * The raw registry code, or `null` for orders whose reason predates
   * TASK-4744 and was never recorded.
   */
  code: string | null;
  /** False when the code has no `orders.geocodeErrors.*` entry in this bundle. */
  known: boolean;
  /** Translated reason heading — never a raw key path, never blank. */
  label: string;
  /** Display labels for the orders in this group, in payload order. */
  orderLabels: string[];
  /** The items themselves, for callers needing ids or addresses. */
  items: GeocodingFailureItem[];
}

/** The whole breakdown, ready to render. */
export interface GeocodeFailureBreakdown {
  groups: GeocodeFailureGroup[];
  /** Total failed orders, including any the backend's item cap omitted. */
  total: number;
  /** True when the itemized list does not name every failed order. */
  truncated: boolean;
  /** Failed orders not named anywhere in `groups`. */
  hiddenCount: number;
}

/**
 * Display label for one failed order: title → address → `#id`.
 *
 * Mirrors `unassignedOrderLabel`. Never returns an empty string, so a row is
 * never a blank line — and since the address is the thing the dispatcher has to
 * FIX here, it is the more useful fallback, not merely a filler.
 */
export function geocodeFailureLabel(item: GeocodingFailureItem): string {
  const title = item.order_title?.trim();
  if (title) return title;
  const address = item.address?.trim();
  if (address) return address;
  return `#${item.order_id}`;
}

/**
 * Translates a raw geocoding code into a reason sentence.
 *
 * Three distinct outcomes, none of which is ever a bare key path:
 *  - a known code → its own copy;
 *  - `null` → "the reason was not recorded", the honest answer for a row that
 *    failed before TASK-4744 shipped;
 *  - an unknown code → prose that interpolates the raw code, so the dispatcher
 *    still learns something specific and support can grep for it. This is the
 *    BUG-448 shape: `check:i18n` cannot see these dynamic lookups, so the
 *    rendered-text tests are what keep this branch honest.
 */
export function translateGeocodeErrorCode(
  code: string | null | undefined,
  t: TFunction,
): string {
  const trimmed = code?.trim();
  if (!trimmed) return t("orders.geocodeErrors.unrecorded");
  if (isKnownGeocodeErrorCode(trimmed)) {
    return t(`orders.geocodeErrors.${trimmed}`);
  }
  return t("orders.geocodeErrors.unknown", { code: trimmed });
}

/**
 * Narrows an untyped progress payload to the failure items.
 *
 * Defensive on purpose: a frontend can poll a backend older than TASK-4744,
 * where `failed_items` is simply absent, and a malformed row must not blow up
 * the poller's terminal branch.
 */
export function parseGeocodeFailureItems(
  rawItems: unknown,
): GeocodingFailureItem[] {
  if (!Array.isArray(rawItems)) return [];
  return rawItems.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const record = entry as Record<string, unknown>;
    const orderId = Number(record.order_id);
    if (!Number.isFinite(orderId)) return [];
    return [
      {
        order_id: orderId,
        order_title:
          typeof record.order_title === "string" ? record.order_title : null,
        address: typeof record.address === "string" ? record.address : "",
        code: typeof record.code === "string" ? record.code : null,
        message: typeof record.message === "string" ? record.message : null,
      },
    ];
  });
}

/**
 * Groups failed orders by reason code and translates each heading.
 *
 * Group ORDER is size descending — the dominant cause reads first, which is the
 * question the dispatcher actually asked ("is this one bad address or is the
 * provider down?"). Ties break on the label so the order is stable rather than
 * dependent on payload iteration order.
 *
 * `failedTotal` is the UNTRUNCATED count from the endpoint, so `hiddenCount`
 * stays truthful when the backend's 20-item cap dropped names.
 */
export function buildGeocodeFailureBreakdown(
  items: GeocodingFailureItem[],
  failedTotal: number,
  t: TFunction,
): GeocodeFailureBreakdown {
  const byCode = new Map<string | null, GeocodingFailureItem[]>();
  for (const item of items) {
    const key = item.code?.trim() ? item.code.trim() : null;
    const existing = byCode.get(key);
    if (existing) existing.push(item);
    else byCode.set(key, [item]);
  }

  const groups: GeocodeFailureGroup[] = [...byCode.entries()].map(
    ([code, groupItems]) => ({
      code,
      known: code !== null && isKnownGeocodeErrorCode(code),
      label: translateGeocodeErrorCode(code, t),
      orderLabels: groupItems.map(geocodeFailureLabel),
      items: groupItems,
    }),
  );

  groups.sort(
    (a, b) => b.items.length - a.items.length || a.label.localeCompare(b.label),
  );

  const namedCount = items.length;
  const total = Math.max(failedTotal, namedCount);

  return {
    groups,
    total,
    truncated: total > namedCount,
    hiddenCount: Math.max(0, total - namedCount),
  };
}

/**
 * Flattens a breakdown into one plain-text paragraph for a toast description.
 *
 * A toast has no room for a list, so each reason becomes
 * `<reason>: <order>, <order>` and the reasons are joined by `; `, with the
 * omitted remainder appended once at the end. Text-only because `toastWarning`
 * takes a `description` STRING (TASK-4739) — the same linearization
 * `formatUnassignedBreakdownText` performs, so the two surfaces read alike.
 */
export function formatGeocodeFailureText(
  breakdown: GeocodeFailureBreakdown,
  t: TFunction,
): string {
  const parts = breakdown.groups.map(
    (group) => `${group.label}: ${group.orderLabels.join(", ")}`,
  );
  if (breakdown.hiddenCount > 0) {
    parts.push(t("toast.geocodeFailedMore", { count: breakdown.hiddenCount }));
  }
  return parts.join("; ");
}
